import AsyncSelect from 'react-select/async'
import Select from 'react-select'
import { FilterPopover } from '@/shared/components/filter-popover'
import { store } from '@/store'
import {
  companyApi,
  contractApi,
  officeApi,
  useGetOfficesQuery,
  userApi
} from '@/store/services'
import { toOptions } from '@/utils'
import {
  Link,
  createSearchParams,
  generatePath,
  useSearchParams
} from 'react-router-dom'
import { Button, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { accountServices } from '@/services'

export const DashboardFilter = () => {
  const [managments, setManagments] = useState([])
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedManagment, setSelectedManagment] = useState({})
  const [selectedFilter, setSelectedFilter] = useState({})
  const { t } = useTranslation()

  const companyLoadOptions = (inputValue, callback) => {
    const request = store
      .dispatch(
        companyApi.endpoints.getCompanySelector.initiate(
          '?' +
            createSearchParams({
              q: inputValue,
              per_page: 10000
            }).toString() +
            '&order[field]=name&order[sort]=asc'
        )
      )
      .unwrap()

    request.then(({ companies }) => {
      callback(toOptions({ data: companies }))
    })
  }

  const contractLoadOptions = (inputValue, callback) => {
    const normalizedInput = (inputValue ?? '').trim()
    const isNumericSearch = normalizedInput !== '' && /^\d+$/.test(normalizedInput)

    const request = store
      .dispatch(
        contractApi.endpoints.getParentsSelectorContracts.initiate(
          '?' +
            createSearchParams({
              q: inputValue,
              per_page: 10000,
              ...(isNumericSearch ? { id: normalizedInput } : {})
            }).toString() +
            '&order[field]=id&order[sort]=asc'
        )
      )
      .unwrap()

    request.then(({ contracts }) => {
      callback(
        contracts.map((contract) => ({
          value: contract.id,
          label: `${contract.id} - ${contract.name || 'Sin nombre'} - ${contract.companyName || 'Sin contratista'}`
        }))
      )
    })
  }

  const officeLoadOptions = (inputValue, callback) => {
    const request = store
      .dispatch(
        officeApi.endpoints.getOffices.initiate(
          '?' +
            createSearchParams({
              q: inputValue,
              per_page: 20
            }).toString() +
            '&order[field]=name&order[sort]=asc'
        )
      )
      .unwrap()

    request.then(({ offices }) => {
      callback(toOptions({ data: offices }))
    })
  }

  const userLoadOptions = (inputValue, callback) => {
    const request = store
      .dispatch(
        userApi.endpoints.getUsers.initiate(
          '?' +
            createSearchParams({
              q: inputValue,
              role: 'Administrador de contrato'
            }).toString() +
            '&order[field]=first_name&order[sort]=asc'
        )
      )
      .unwrap()

    request.then(({ users }) => {
      callback(toOptions({ data: users }))
    })
  }

  const getManagments = (callback) => {
    const request = accountServices.getManagments()

    request.then(({ data: { managments } }) => {
      callback(toOptions({ data: managments }))
    })
  }

  const subManagmentOptions = useMemo(() => {
    return toOptions({ data: selectedManagment?.resource?.subManagments ?? [] })
  }, [selectedManagment])

  const onChangeFilter = ({ key, selectOption }) => {
    const { value } = selectOption
    setSelectedFilter(selectOption)
    const cleanSearchParams = new URLSearchParams()
    cleanSearchParams.set(key, value)
    setSearchParams(cleanSearchParams)
  }

  const clearStates = () => {
    setSelectedManagment({})
    setSelectedFilter({})
  }

  useEffect(() => {
    getManagments(setManagments)
  }, [])

  return (
    <FilterPopover popoverContentProps={{ className: 'p-5 w-1/2' }}>
      <div className='grid grid-cols-2 gap-4'>
        <div className='flex flex-col'>
          <Typography className='text-sm'>{t('company.one')}</Typography>
          <AsyncSelect
            onChange={(selectOption) =>
              onChangeFilter({ key: 'company_id', selectOption })
            }
            defaultOptions
            loadOptions={companyLoadOptions}
            placeholder='Seleccione un contratista'
            value={searchParams.get('company_id') ? selectedFilter : null}
          />
        </div>

        <div className='flex flex-col'>
          <Typography className='text-sm'>Gerencia</Typography>
          <Select
            onChange={(selectOption) => {
              setSelectedManagment(selectOption)
              onChangeFilter({ key: 'managment_id', selectOption })
            }}
            options={managments}
            placeholder='Seleccione una gerencia'
            value={
              searchParams.get('managment_id') ||
              searchParams.get('submanagment_id')
                ? selectedManagment
                : null
            }
          />
        </div>

        <div className='flex flex-col'>
          <Typography className='text-sm'>{t('contract.one')}</Typography>
          <AsyncSelect
            onChange={(selectOption) =>
              onChangeFilter({ key: 'contract_id', selectOption })
            }
            defaultOptions
            loadOptions={contractLoadOptions}
            placeholder='Seleccione un contrato'
            value={searchParams.get('contract_id') ? selectedFilter : null}
          />
        </div>

        <div className='flex flex-col'>
          <Typography className='text-sm'>Sub Gerencia</Typography>
          <Select
            onChange={(selectOption) => {
              onChangeFilter({ key: 'submanagment_id', selectOption })
            }}
            options={subManagmentOptions}
            isDisabled={!selectedManagment.value}
            placeholder='Seleccione una subgerencia'
            value={searchParams.get('submanagment_id') ? selectedFilter : null}
          />
        </div>

        <div className='flex flex-col'>
          <Typography className='text-sm'>Administrador de contrato</Typography>
          <AsyncSelect
            onChange={(selectOption) =>
              onChangeFilter({ key: 'user_id', selectOption })
            }
            defaultOptions
            loadOptions={userLoadOptions}
            placeholder='Seleccione un administrador de contrato'
            value={searchParams.get('user_id') ? selectedFilter : null}
          />
        </div>

        <div className='flex flex-col'>
          <Typography className='text-sm'>{t('office.one')}</Typography>
          <AsyncSelect
            onChange={(selectOption) =>
              onChangeFilter({ key: 'office_id', selectOption })
            }
            defaultOptions
            loadOptions={officeLoadOptions}
            placeholder='Seleccione una dependencia'
            value={searchParams.get('office_id') ? selectedFilter : null}
          />
        </div>
      </div>

      <div className='flex justify-center mt-10'>
        <Link to={generatePath('/dashboard')}>
          <Button
            size='sm'
            type='button'
            color='red'
            variant='outlined'
            onClick={clearStates}
          >
            {t('filters.delete')}
          </Button>
        </Link>
      </div>
    </FilterPopover>
  )
}
